/**
 * D4 · Playback with pitch overlay at 0.75×/1×.
 * D5/D6 · Time-stamped text & audio (voice-note) comments.
 * D7/D8 · Mark reviewed + quick reactions.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { RenderedExercise, TracePoint } from '@solfo/shared';
import { api, fetchFileUrl, uploadFile } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { Button, Card, ErrorText, Input, Spinner, Tag, fmtDateTime } from '../../lib/ui.tsx';
import { exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';
import PitchCanvas from '../../components/PitchCanvas.tsx';

interface TakeDetail {
  id: string;
  status: string;
  fileId: string;
  pitchTraceJson: string | null;
  refinedTraceJson: string | null;
  refinedScore: number | null;
  analysisStatus?: string;
  targetJson: string | null;
  syncOffsetSec: number | null;
  accuracyScore: number | null;
  submittedAt: string | null;
  keepForever: boolean;
  student?: { id: string; name: string };
  exercise?: { id: string; name: string };
  comments: {
    id: string;
    atSec: number;
    text: string | null;
    audioFileId: string | null;
    author: { id: string; name: string; role: string };
  }[];
}

export function useTakePlayback(take: TakeDetail | undefined) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);

  useEffect(() => {
    if (take?.fileId) void fetchFileUrl(take.fileId).then(setAudioUrl).catch(() => {});
  }, [take?.fileId]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.playbackRate = rate;
    // Chrome reports duration=Infinity for streamed MediaRecorder webm until
    // forced to scan the file — seek far past the end once, then rewind.
    const fixInfiniteDuration = () => {
      const onSeeked = () => {
        el.removeEventListener('seeked', onSeeked);
        if (Number.isFinite(el.duration)) setDuration(el.duration);
        el.currentTime = 0;
      };
      el.addEventListener('seeked', onSeeked);
      el.currentTime = 1e10;
    };
    let fixAttempted = false;
    const onMeta = () => {
      if (Number.isFinite(el.duration)) setDuration(el.duration);
      else if (!fixAttempted) {
        fixAttempted = true;
        fixInfiniteDuration();
      }
    };
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('durationchange', onMeta);
    if (el.readyState >= 1) onMeta();
    let raf = 0;
    const tick = () => {
      if (!el.seeking) setTime(el.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('durationchange', onMeta);
      cancelAnimationFrame(raf);
    };
  }, [rate, audioUrl]);

  /**
   * The recording starts before the count-in, but the pitch trace/target
   * timeline starts at beat 1. Shift audio time by the stored offset so the
   * overlay playhead matches what is heard; for takes recorded before the
   * offset existed, estimate the lead-in from file length vs exercise length.
   */
  const syncOffset = useMemo(() => {
    if (!take) return 0;
    if (take.syncOffsetSec !== null && take.syncOffsetSec !== undefined) return take.syncOffsetSec;
    if (take.targetJson && duration > 0) {
      const total = (JSON.parse(take.targetJson) as RenderedExercise).totalSec;
      if (duration > total) return Math.max(0, duration - total - 0.5);
    }
    return 0;
  }, [take, duration]);

  // Cue playback to beat 1: the file's lead-in (count-in + recorder startup)
  // is dead air for the reviewer, so pressing play should start where the
  // exercise starts. Done once per file — manual seeks are never overridden.
  const cuedRef = useRef<string | null>(null);
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !audioUrl || syncOffset <= 0 || cuedRef.current === audioUrl) return;
    const cue = () => {
      if (cuedRef.current === audioUrl) return;
      if (el.readyState >= 1 && el.paused && !el.seeking && el.currentTime < syncOffset) {
        cuedRef.current = audioUrl;
        el.currentTime = syncOffset;
      }
    };
    cue();
    el.addEventListener('canplay', cue);
    el.addEventListener('seeked', cue); // after the Infinity-fix rewind lands on 0
    return () => {
      el.removeEventListener('canplay', cue);
      el.removeEventListener('seeked', cue);
    };
  }, [audioUrl, syncOffset]);

  const exerciseTime = time - syncOffset;

  return { audioUrl, audioRef, time, exerciseTime, rate, setRate };
}

export function CommentList({
  comments,
  onJump,
}: {
  comments: TakeDetail['comments'];
  onJump: (t: number) => void;
}) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    for (const c of comments) {
      if (c.audioFileId && !urls[c.id]) {
        void fetchFileUrl(c.audioFileId).then((u) => setUrls((s) => ({ ...s, [c.id]: u })));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments]);

  if (comments.length === 0) return <p className="text-xs text-slate-400">No comments yet.</p>;
  return (
    <ul className="space-y-2">
      {comments.map((c) => (
        <li key={c.id} className="rounded-lg bg-slate-50 p-2">
          <button onClick={() => onJump(c.atSec)} className="font-mono text-xs font-semibold text-brand-700">
            {fmtClock(c.atSec)}
          </button>
          <span className="ml-2 text-xs text-slate-400">{c.author.name}</span>
          {c.text && <p className="mt-1 text-sm">{c.text}</p>}
          {c.audioFileId && urls[c.id] && <audio controls src={urls[c.id]} className="mt-1 h-8 w-full" />}
        </li>
      ))}
    </ul>
  );
}

export function fmtClock(sec: number): string {
  return `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
}

export default function TakeReview() {
  const { takeId } = useParams<{ takeId: string }>();
  const { me } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [commentText, setCommentText] = useState('');
  const [recordingComment, setRecordingComment] = useState<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [error, setError] = useState<unknown>(null);

  const { data: take, isLoading } = useQuery({
    queryKey: ['take', takeId],
    queryFn: () => api<TakeDetail>(`/api/takes/${takeId}`),
  });
  const { audioUrl, audioRef, time, exerciseTime, rate, setRate } = useTakePlayback(take);

  const rendered = take?.targetJson ? (JSON.parse(take.targetJson) as RenderedExercise) : null;
  // prefer the server-refined trace (octave flips corrected) for the overlay
  const traceJson = take?.refinedTraceJson ?? take?.pitchTraceJson;
  const trace = traceJson ? (JSON.parse(traceJson) as TracePoint[]) : [];

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['take', takeId] });
    void qc.invalidateQueries({ queryKey: ['review-queue'] });
  };

  const comment = useMutation({
    mutationFn: (body: { atSec: number; text?: string; audioFileId?: string }) =>
      api(`/api/takes/${takeId}/comments`, { body }),
    onSuccess: () => {
      setCommentText('');
      invalidate();
    },
    onError: setError,
  });

  const review = useMutation({
    mutationFn: (reaction: string | null) => api(`/api/takes/${takeId}/review`, { body: { reaction } }),
    onSuccess: () => {
      invalidate();
      navigate('/coach/review');
    },
    onError: setError,
  });

  const keep = useMutation({
    mutationFn: (k: boolean) => api(`/api/takes/${takeId}/keep`, { body: { keep: k } }),
    onSuccess: invalidate,
  });

  async function toggleVoiceComment() {
    if (recordingComment) {
      recordingComment.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      const atSec = audioRef.current?.currentTime ?? 0;
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecordingComment(null);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType.split(';')[0] });
        const file = await uploadFile(blob, `comment-${Date.now()}.webm`);
        comment.mutate({ atSec, audioFileId: file.id });
      };
      rec.start();
      setRecordingComment(rec);
    } catch (err) {
      setError(err);
    }
  }

  if (isLoading) return <Spinner />;
  if (!take) return <ErrorText error="Take not found" />;
  const isCoach = me?.role === 'coach';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold">{exLabel(take.exercise)}</h1>
          <p className="text-xs text-slate-500">
            {take.student?.name} · submitted {fmtDateTime(take.submittedAt)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {take.accuracyScore !== null && <Tag tone="brand">score {take.refinedScore ?? take.accuracyScore}</Tag>}
          {take.refinedTraceJson && <Tag tone="green">refined ✓</Tag>}
          {take.status === 'reviewed' && <Tag tone="green">reviewed</Tag>}
        </div>
      </div>

      {/* D4 · pitch overlay */}
      {rendered ? (
        <PitchCanvas rendered={rendered} trace={trace} currentTime={exerciseTime} mode="full" height={240} />
      ) : (
        <Card>
          <p className="text-xs text-slate-400">No pitch trace for this take (custom-audio exercise).</p>
        </Card>
      )}

      <Card>
        {audioUrl ? (
          <div className="flex items-center gap-3">
            <audio ref={audioRef} controls src={audioUrl} className="h-10 w-full" />
            <div className="flex shrink-0 gap-1">
              {[0.75, 1].map((r) => (
                <button
                  key={r}
                  onClick={() => setRate(r)}
                  className={`rounded-md px-2 py-1 text-xs font-medium ${rate === r ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                >
                  {r}×
                </button>
              ))}
            </div>
          </div>
        ) : (
          <Spinner label="Loading audio…" />
        )}
      </Card>

      <Card title={`Comments (pinned to the moment)`}>
        <CommentList
          comments={take.comments}
          onJump={(t) => {
            if (audioRef.current) audioRef.current.currentTime = t;
          }}
        />
        <div className="mt-3 flex gap-2">
          <span className="mt-2 shrink-0 font-mono text-xs text-slate-500">{fmtClock(time)}</span>
          <Input
            placeholder="e.g. you're going flat entering the fifth"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && commentText.trim()) {
                comment.mutate({ atSec: audioRef.current?.currentTime ?? 0, text: commentText.trim() });
              }
            }}
          />
          <Button
            variant="secondary"
            disabled={!commentText.trim()}
            onClick={() => comment.mutate({ atSec: audioRef.current?.currentTime ?? 0, text: commentText.trim() })}
          >
            Pin
          </Button>
          {/* D6 · voice note */}
          <Button variant={recordingComment ? 'danger' : 'secondary'} onClick={toggleVoiceComment}>
            {recordingComment ? '■ Stop' : '🎙️'}
          </Button>
        </div>
      </Card>

      {isCoach && (
        <Card title="Finish review">
          <div className="flex flex-wrap gap-2">
            {/* D8 · quick reactions */}
            <Button onClick={() => review.mutate('great')} disabled={take.status !== 'submitted'}>
              Great take ✓
            </Button>
            <Button variant="secondary" onClick={() => review.mutate('nice_progress')} disabled={take.status !== 'submitted'}>
              Nice progress 📈
            </Button>
            <Button variant="secondary" onClick={() => review.mutate('keep_going')} disabled={take.status !== 'submitted'}>
              Keep going 💪
            </Button>
            <Button variant="secondary" onClick={() => review.mutate(null)} disabled={take.status !== 'submitted'}>
              Mark reviewed (D7)
            </Button>
            {/* D10 · retention exception */}
            <Button variant="ghost" onClick={() => keep.mutate(!take.keepForever)}>
              {take.keepForever ? '★ Kept forever' : '☆ Keep this one'}
            </Button>
            {take.student && take.exercise && (
              <Link to={`/coach/compare?student=${take.student.id}&exercise=${take.exercise.id}&a=${take.id}`}>
                <Button variant="ghost">Compare with an older take →</Button>
              </Link>
            )}
          </div>
        </Card>
      )}
      <ErrorText error={error} />
    </div>
  );
}
